// src/components/policies/PlanHistoryCard.jsx
// Displays a client's plan history (past Medicare plans)

import React, { useState, useEffect } from 'react';
import { History, Calendar, Building2, FileText, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const PLAN_STATUS_STYLES = {
  past: 'bg-slate-100 text-slate-600',
  current: 'bg-green-100 text-green-700',
  future: 'bg-blue-100 text-blue-700'
};

const TRANSITION_SOURCE_LABELS = {
  import: 'Import',
  manual: 'Manual Update',
  auto_transition: 'Auto Transition',
  bluebutton: 'Blue Button Sync'
};

export default function PlanHistoryCard({ clientId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchPlanHistory();
  }, [clientId]);

  const fetchPlanHistory = async () => {
    try {
      setLoading(true);
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${API_BASE}/clients/${clientId}/plan-history`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.ok) {
        setHistory(data.planHistory || []);
      } else {
        setError(data.error || 'Failed to load plan history');
      }
    } catch (err) {
      console.error('[PlanHistoryCard] Error:', err);
      setError('Failed to load plan history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading plan history...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-red-200 rounded-xl p-4 bg-red-50">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <History className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <h4 className="font-medium text-slate-600">No Plan History</h4>
            <p className="text-sm text-slate-500">Past plans will appear here when updated</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <History className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h4 className="font-medium text-slate-800">Plan History</h4>
            <p className="text-sm text-slate-500">{history.length} past plan{history.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-slate-200 divide-y divide-slate-100">
          {history.map((plan) => (
            <div key={plan.id} className="p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mt-0.5">
                    <Building2 className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-800">{plan.carrier || 'Unknown Carrier'}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${PLAN_STATUS_STYLES[plan.planStatus] || PLAN_STATUS_STYLES.past}`}>
                        {plan.planStatus || 'Past'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">{plan.plan || 'Unknown Plan'}</p>
                    {plan.planType && (
                      <p className="text-xs text-slate-500 mt-0.5">{plan.planType}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Plan Details */}
              <div className="mt-3 ml-11 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <span className="text-slate-500">Effective:</span>
                  <span className="ml-1 text-slate-700">{formatDate(plan.effectiveDate)}</span>
                </div>
                {plan.terminationDate && (
                  <div>
                    <span className="text-slate-500">Terminated:</span>
                    <span className="ml-1 text-slate-700">{formatDate(plan.terminationDate)}</span>
                  </div>
                )}
                {plan.monthlyPremium && (
                  <div>
                    <span className="text-slate-500">Premium:</span>
                    <span className="ml-1 text-slate-700">{formatCurrency(plan.monthlyPremium)}/mo</span>
                  </div>
                )}
                {plan.transitionSource && (
                  <div>
                    <span className="text-slate-500">Source:</span>
                    <span className="ml-1 text-slate-700">{TRANSITION_SOURCE_LABELS[plan.transitionSource] || plan.transitionSource}</span>
                  </div>
                )}
              </div>

              {/* Notes */}
              {plan.notes && (
                <div className="mt-2 ml-11">
                  <div className="flex items-start gap-1.5 text-sm">
                    <FileText className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <p className="text-slate-600">{plan.notes}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
